// File: src/Components/payments/parts/CreditCardForm.jsx

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    TextField,
    InputAdornment,
} from '@mui/material';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import { FaCcVisa, FaCcMastercard, FaCcAmex, FaCreditCard } from 'react-icons/fa';
import styles from './CreditCardForm.module.scss';

const CARD_NUMBER_PLACEHOLDER = '3567 8000 9998 8877';
const CARD_NUMBER_MAX_DIGITS = 16;

const detectCardType = (number) => {
    const cleaned = number.replace(/\s+/g, '');
    if (/^4/.test(cleaned)) return 'Visa';
    if (/^5[1-5]/.test(cleaned)) return 'MasterCard';
    if (/^3[47]/.test(cleaned)) return 'American Express';
    return '';
};

const formatCardNumber = (input) => {
    const digits = input.replace(/\D/g, '').slice(0, CARD_NUMBER_MAX_DIGITS);
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ');
};

const getCardIcon = (type) => {
    switch (type) {
        case 'Visa':
            return <FaCcVisa className={styles.cardIcon} style={{ color: '#1a1f71' }} />;
        case 'MasterCard':
            return <FaCcMastercard className={styles.cardIcon} style={{ color: '#eb001b' }} />;
        case 'American Express':
            return <FaCcAmex className={styles.cardIcon} style={{ color: '#2e77bb' }} />;
        default:
            return <FaCreditCard className={styles.cardIcon} />;
    }
};

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => {
    const value = String(i + 1).padStart(2, '0');
    return { value, label: value };
});

const digitsOnly = (value, maxLength) => value.replace(/\D/g, '').slice(0, maxLength);

const parseMonthValue = (raw) => {
    if (!raw) return null;
    const nextMonth = Number(raw);
    if (!Number.isInteger(nextMonth) || nextMonth < 1 || nextMonth > 12) return null;
    return String(nextMonth).padStart(2, '0');
};

const shortYearToFullLabel = (shortYear) => {
    if (!shortYear) return '';
    const yy = String(shortYear).padStart(2, '0');
    if (!/^\d{2}$/.test(yy)) return '';
    const currentCentury = Math.floor(new Date().getFullYear() / 100) * 100;
    return String(currentCentury + Number(yy));
};

const parseYearValue = (raw) => {
    if (!raw) return null;
    if (/^\d{2}$/.test(raw)) {
        const yy = Number(raw);
        if (yy < 0 || yy > 99) return null;
        return String(yy).padStart(2, '0');
    }
    if (/^\d{4}$/.test(raw)) {
        return String(Number(raw) % 100).padStart(2, '0');
    }
    return null;
};

const maskCardPreview = (cardNumber) => {
    const digits = (cardNumber || '').replace(/\D/g, '').slice(0, CARD_NUMBER_MAX_DIGITS);
    if (!digits) return '•••• •••• •••• ••••';
    const padded = digits.padEnd(CARD_NUMBER_MAX_DIGITS, '•');
    return padded.replace(/(.{4})(?=.)/g, '$1 ');
};

const CreditCardPreview = ({ cardNumber, month, year, holder, cvv, cardType, showCvvSide, t }) => {
    const expiry =
        month && year
            ? `${String(month).padStart(2, '0')}/${String(year).padStart(2, '0')}`
            : 'MM/YY';
    const cvvDisplay = (cvv || '').padEnd(3, '•');

    return (
        <div className={styles.previewScene} aria-hidden="true" dir="ltr">
            <div className={`${styles.previewFlip} ${showCvvSide ? styles.previewFlipCvv : ''}`}>
                <div className={`${styles.previewCard} ${styles.previewFront}`}>
                    <div className={styles.previewGlow} />
                    <div className={styles.previewTop}>
                        <span className={styles.previewBrand}>CreditCard</span>
                        <span className={styles.previewLogo}>{getCardIcon(cardType)}</span>
                    </div>
                    <div className={styles.previewChip} />
                    <div className={styles.previewNumber}>{maskCardPreview(cardNumber)}</div>
                    <div className={styles.previewBottom}>
                        <div className={styles.previewMeta}>
                            <span className={styles.previewMetaLabel}>{t('CardHolder', { defaultValue: 'CARD HOLDER' })}</span>
                            <span className={styles.previewMetaValue}>
                                {(holder || '').trim() || '—'}
                            </span>
                        </div>
                        <div className={styles.previewMeta}>
                            <span className={styles.previewMetaLabel}>{t('ExpirationDate')}</span>
                            <span className={styles.previewMetaValue}>{expiry}</span>
                        </div>
                    </div>
                </div>

                <div className={`${styles.previewCard} ${styles.previewBack}`}>
                    <div className={styles.previewGlow} />
                    <div className={styles.previewMagstripe} />
                    <div className={styles.previewSignatureRow}>
                        <div className={styles.previewSignatureStrip} />
                        <div className={styles.previewCvvBox}>
                            <span className={styles.previewCvvLabel}>{t('CVV')}</span>
                            <span className={styles.previewCvvValue}>{cvvDisplay}</span>
                        </div>
                    </div>
                    <div className={styles.previewBackFooter}>
                        <span className={styles.previewLogo}>{getCardIcon(cardType)}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ExpiryEditableSelect = ({
    field,
    value,
    displayValue,
    placeholder,
    options,
    maxLength,
    disabled,
    hasError,
    openField,
    setOpenField,
    focusedField,
    setFocusedField,
    onCommit,
    ariaLabel,
}) => {
    const fieldRef = useRef(null);
    const listRef = useRef(null);
    const [inputValue, setInputValue] = useState(displayValue);
    const [menuStyle, setMenuStyle] = useState(null);
    const isOpen = openField === field;
    const isFocused = focusedField === field;

    useEffect(() => {
        setInputValue(displayValue);
    }, [displayValue]);

    const updateMenuPosition = () => {
        const el = fieldRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const viewportPad = 8;
        const maxHeight = 220;
        const spaceBelow = window.innerHeight - rect.bottom - viewportPad;
        const spaceAbove = rect.top - viewportPad;
        const openUp = spaceBelow < 140 && spaceAbove > spaceBelow;
        const height = Math.min(maxHeight, openUp ? spaceAbove : spaceBelow);

        setMenuStyle({
            position: 'fixed',
            top: openUp ? undefined : rect.bottom + 6,
            bottom: openUp ? window.innerHeight - rect.top + 6 : undefined,
            left: rect.left,
            width: rect.width,
            maxHeight: Math.max(height, 96),
            zIndex: 1400,
        });
    };

    useLayoutEffect(() => {
        if (!isOpen || disabled) {
            setMenuStyle(null);
            return undefined;
        }
        updateMenuPosition();
        const onReposition = () => updateMenuPosition();
        window.addEventListener('resize', onReposition);
        window.addEventListener('scroll', onReposition, true);
        return () => {
            window.removeEventListener('resize', onReposition);
            window.removeEventListener('scroll', onReposition, true);
        };
    }, [isOpen, disabled]);

    useEffect(() => {
        if (!isOpen) return undefined;

        const handlePointerDown = (event) => {
            const inList = listRef.current?.contains(event.target);
            const onChevron = event.target.closest('[data-expiry-chevron="true"]');
            // Dropdown stays open only while interacting with the list or the chevron.
            if (!inList && !onChevron) setOpenField(null);
        };
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') setOpenField(null);
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, setOpenField]);

    const commit = ({ revertIfInvalid = false } = {}) => {
        const next = onCommit(inputValue);
        if (next == null) {
            if (revertIfInvalid) setInputValue(displayValue);
            return;
        }
        setInputValue(field === 'year' ? shortYearToFullLabel(next) || next : next);
    };

    const handleInputChange = (raw) => {
        const nextRaw = digitsOnly(raw, maxLength);
        setInputValue(nextRaw);
        if (nextRaw.length === maxLength) {
            const next = onCommit(nextRaw);
            if (next != null) {
                setInputValue(field === 'year' ? shortYearToFullLabel(next) || next : next);
            }
        }
    };

    const handleSelect = (optionValue) => {
        const next = onCommit(optionValue);
        if (next != null) {
            setInputValue(field === 'year' ? shortYearToFullLabel(next) || next : next);
        }
        setOpenField(null);
    };

    const list =
        isOpen && !disabled && menuStyle
            ? createPortal(
                <div
                    ref={listRef}
                    className={styles.expiryListPortal}
                    role="listbox"
                    style={menuStyle}
                >
                    {options.map((option) => {
                        const selected = option.value === value;
                        return (
                            <button
                                type="button"
                                key={option.value}
                                role="option"
                                aria-selected={selected}
                                className={`${styles.expiryOption} ${selected ? styles.expiryOptionSelected : ''}`}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => handleSelect(option.value)}
                                ref={(node) => {
                                    if (selected && node) {
                                        node.scrollIntoView({ block: 'center', inline: 'nearest' });
                                    }
                                }}
                            >
                                {option.label}
                            </button>
                        );
                    })}
                </div>,
                document.body
            )
            : null;

    return (
        <div
            ref={fieldRef}
            className={`${styles.expiryField} ${isOpen ? styles.expiryFieldOpen : ''} ${isFocused ? styles.expiryFieldFocused : ''} ${hasError ? styles.expiryFieldError : ''} ${disabled ? styles.expiryFieldDisabled : ''}`}
        >
            <div className={styles.expiryTrigger}>
                <input
                    className={styles.expiryInput}
                    value={inputValue}
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={maxLength}
                    placeholder={placeholder}
                    aria-label={ariaLabel}
                    disabled={disabled}
                    onFocus={() => {
                        setFocusedField(field);
                        // Typing mode: never keep/open the dropdown from input focus.
                        if (openField === field) setOpenField(null);
                    }}
                    onChange={(event) => handleInputChange(event.target.value)}
                    onBlur={() => {
                        setFocusedField(null);
                        commit({ revertIfInvalid: true });
                    }}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            event.preventDefault();
                            commit({ revertIfInvalid: true });
                            event.currentTarget.blur();
                        }
                        if (event.key === 'Escape') {
                            event.preventDefault();
                            setInputValue(displayValue);
                            setOpenField(null);
                            event.currentTarget.blur();
                        }
                    }}
                />
                <button
                    type="button"
                    className={styles.expiryChevronBtn}
                    data-expiry-chevron="true"
                    aria-label={ariaLabel}
                    aria-expanded={isOpen}
                    disabled={disabled}
                    onMouseDown={(event) => {
                        // Keep input from stealing focus before toggle.
                        event.preventDefault();
                    }}
                    onClick={() => {
                        if (disabled) return;
                        setOpenField((current) => (current === field ? null : field));
                    }}
                >
                    <span className={styles.expiryChevron} aria-hidden="true" />
                </button>
            </div>
            {list}
        </div>
    );
};

const CreditCardForm = ({ values, onChange, disabled, errors, direction = 'rtl' }) => {
    const { t } = useTranslation();
    const [cardNumberMaxReached, setCardNumberMaxReached] = useState(false);
    const [cvvFocused, setCvvFocused] = useState(false);
    const [openExpiryField, setOpenExpiryField] = useState(null);
    const [focusedExpiryField, setFocusedExpiryField] = useState(null);
    const cardType = detectCardType(values.cardNumber);
    const digitCount = (values.cardNumber || '').replace(/\D/g, '').length;

    const yearOptions = useMemo(() => {
        const currentFullYear = new Date().getFullYear();
        return Array.from({ length: 16 }, (_, i) => {
            const fullYear = currentFullYear + i;
            const shortYear = String(fullYear).slice(-2);
            return { value: shortYear, label: String(fullYear) };
        });
    }, []);

    const inputPropsStyle = {
        disableUnderline: true,
        className: `custom-input-box-sizing-toggle ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
        sx: {
            height: '42px',
            padding: '0 14px',
            borderRadius: 'var(--xtram-radius-md)',
            background: 'var(--login-input-bg)',
            border: 'none',
            boxSizing: 'border-box',
            '&:hover': {
                background: 'color-mix(in srgb, var(--login-input-bg) 85%, var(--xtram-mirage))',
            },
            '&.Mui-focused, &:focus-within': {
                background: 'var(--xtram-white)',
            },
        },
    };

    const emitChange = (name, value) => {
        onChange({ target: { name, value } });
    };

    const handleCardNumberChange = (rawValue) => {
        const digits = rawValue.replace(/\D/g, '');
        if (digits.length > CARD_NUMBER_MAX_DIGITS) {
            setCardNumberMaxReached(true);
            emitChange('cardNumber', formatCardNumber(digits.slice(0, CARD_NUMBER_MAX_DIGITS)));
            return;
        }
        setCardNumberMaxReached(false);
        emitChange('cardNumber', formatCardNumber(digits));
    };

    const commitMonth = (raw) => {
        const next = parseMonthValue(raw);
        if (next == null) return null;
        if (next !== String(values.month || '').padStart(2, '0')) {
            emitChange('month', next);
        }
        return next;
    };

    const commitYear = (raw) => {
        const next = parseYearValue(raw);
        if (next == null) return null;
        if (next !== String(values.year || '').padStart(2, '0')) {
            emitChange('year', next);
        }
        return next;
    };

    const showMaxHint = cardNumberMaxReached || digitCount >= CARD_NUMBER_MAX_DIGITS;
    const monthDisplay = values.month ? String(values.month).padStart(2, '0') : '';
    const yearDisplay = values.year ? shortYearToFullLabel(values.year) : '';

    return (
        <div className={styles.form} dir={direction}>
            <h3 className={styles.formTitle}>
                <InfoOutlined fontSize="small" />
                {t('CreditCardDetails')}
            </h3>

            <hr className={styles.divider} />

            <CreditCardPreview
                cardNumber={values.cardNumber}
                month={values.month}
                year={values.year}
                holder={values.holder}
                cvv={values.cvv}
                cardType={cardType}
                showCvvSide={cvvFocused}
                t={t}
            />

            <div className={styles.stack}>
                <div className={styles.field}>
                    <div className={styles.titleRow}>
                        <div className={styles.title}>{t('CardNumber')}</div>
                        {showMaxHint && (
                            <span className={styles.maxHintBeside}>
                                {t('CardNumberMaxReached')}
                            </span>
                        )}
                    </div>
                    <TextField
                        fullWidth
                        name="cardNumber"
                        variant="standard"
                        className={`${styles.styleInput} ${styles.cardNumberInput} ${errors.cardNumber ? styles.styleInputError : ''}`}
                        placeholder={CARD_NUMBER_PLACEHOLDER}
                        value={values.cardNumber}
                        onChange={(e) => handleCardNumberChange(e.target.value)}
                        disabled={disabled}
                        error={!!errors.cardNumber}
                        helperText={errors.cardNumber || ''}
                        inputProps={{
                            inputMode: 'numeric',
                            dir: 'ltr',
                            maxLength: CARD_NUMBER_MAX_DIGITS + 3, // spaces between 4 groups
                        }}
                        InputProps={{
                            ...inputPropsStyle,
                            endAdornment: (
                                <InputAdornment position="end">
                                    {getCardIcon(cardType)}
                                </InputAdornment>
                            ),
                        }}
                    />
                </div>

                <div className={styles.expiryRow}>
                    <div className={styles.expiryGroup}>
                        <div className={styles.title}>
                            {t('ExpirationDate')}
                        </div>
                        <div className={styles.expirySelects}>
                            <ExpiryEditableSelect
                                field="month"
                                value={monthDisplay}
                                displayValue={monthDisplay}
                                placeholder="MM"
                                options={MONTH_OPTIONS}
                                maxLength={2}
                                disabled={disabled}
                                hasError={!!errors.month}
                                openField={openExpiryField}
                                setOpenField={setOpenExpiryField}
                                focusedField={focusedExpiryField}
                                setFocusedField={setFocusedExpiryField}
                                onCommit={commitMonth}
                                ariaLabel={t('Month')}
                            />
                            <ExpiryEditableSelect
                                field="year"
                                value={values.year ? String(values.year).padStart(2, '0') : ''}
                                displayValue={yearDisplay}
                                placeholder="YYYY"
                                options={yearOptions}
                                maxLength={4}
                                disabled={disabled}
                                hasError={!!errors.year}
                                openField={openExpiryField}
                                setOpenField={setOpenExpiryField}
                                focusedField={focusedExpiryField}
                                setFocusedField={setFocusedExpiryField}
                                onCommit={commitYear}
                                ariaLabel={t('Year')}
                            />
                        </div>
                        {(errors.month || errors.year) && (
                            <div className={styles.fieldError}>
                                {errors.month || errors.year}
                            </div>
                        )}
                    </div>

                    <div className={styles.cvvGroup}>
                        <div className={styles.title}>{t('CVV')}</div>
                        <TextField
                            fullWidth
                            name="cvv"
                            variant="standard"
                            className={`${styles.styleInput} ${errors.cvv ? styles.styleInputError : ''}`}
                            placeholder="123"
                            type="password"
                            value={values.cvv}
                            onChange={(e) => {
                                emitChange('cvv', e.target.value.replace(/\D/g, '').slice(0, 3));
                            }}
                            onFocus={() => setCvvFocused(true)}
                            onBlur={() => setCvvFocused(false)}
                            disabled={disabled}
                            error={!!errors.cvv}
                            helperText={errors.cvv}
                            inputProps={{
                                inputMode: 'numeric',
                                maxLength: 3,
                                dir: 'ltr',
                            }}
                            InputProps={{
                                ...inputPropsStyle,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <InfoOutlined fontSize="small" sx={{ color: 'var(--xtram-mirage-muted)' }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreditCardForm;
